import {
  findShowcaseSubPageBySlug,
  getShowcaseSubPageSlugFromPath,
  resolveShowcaseSubPageSlugs,
  type ShowcaseSubPage,
  type ShowcaseSubPageWithSlug,
} from '~/components/v2/components/showcase/showcaseSubPageRoutes';

/**
 * Query param that records the sub-page a viewer drilled into a board FROM.
 *
 * A share link's board route is flat — `/a/{shortId}/b/{boardId}` — with no room for the page the
 * viewer came from, and the same board can legitimately be reachable from more than one sub-page,
 * so membership cannot reconstruct the trail after the fact. The prototype answered this with an
 * `itemIds` page-pinning model that has no backend; the shipping model instead records the route
 * the viewer actually took: a board opened from inside a sub-page carries that page's slug, and
 * the breadcrumbs read it back.
 *
 * A board URL without the param — a hard-loaded or shared board link, or any share link with no
 * sub-pages at all — resolves to no sub-page and keeps today's breadcrumbs byte for byte.
 */
export const SHOWCASE_SUB_PAGE_BOARD_QUERY_PARAM = 'page';

/**
 * Reads the sub-page slug back off a board route's query string. Parsed out of the raw path for the
 * same reason the path helpers are: a full path string is correct on the first client render, while
 * the router's `query` object is empty until the router is ready on a statically-generated page.
 * `URLSearchParams` decodes without throwing, so a hand-edited URL degrades to "no sub-page"
 * instead of taking the public page down.
 */
export const getShowcaseSubPageSlugFromBoardPath = (path: string): string | null => {
  const [, queryString] = path.split('?');

  if (!queryString) {
    return null;
  }

  const [searchOnly] = queryString.split('#');

  return new URLSearchParams(searchOnly ?? '').get(SHOWCASE_SUB_PAGE_BOARD_QUERY_PARAM)?.trim() || null;
};

export type FindShowcaseSubPageForPathParams = {
  /** A full share-link path — sub-page route, or board route carrying the query param above. */
  path: string;
  subPages: readonly ShowcaseSubPage[];
};

/**
 * The sub-page a path puts the viewer inside, with its canonical slug: the slug segment of a
 * sub-page route, else the trail param of a board route. Returns null for every other path, so
 * non-sub-page surfaces are unaffected.
 */
export const findShowcaseSubPageForPath = ({
  path,
  subPages,
}: FindShowcaseSubPageForPathParams): ShowcaseSubPageWithSlug | null => {
  const slug = getShowcaseSubPageSlugFromPath(path) ?? getShowcaseSubPageSlugFromBoardPath(path);
  const subPage = findShowcaseSubPageBySlug({ slug, subPages });

  if (!subPage) {
    return null;
  }

  // Re-resolving hands back the SAME sub-page object references, so identity is the precise way to
  // recover the matched page's canonical slug — two sub-pages can share a name and a collectionId,
  // and only the slug distinguishes their routes.
  return resolveShowcaseSubPageSlugs(subPages).find((entry) => entry.subPage === subPage) ?? null;
};
